import {useEffect, useState} from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import {Skeleton} from '@mui/material';
import {Stack} from '@mui/material';

am4core.useTheme(am4themes_animated);
am4core.options.autoSetClassName = true;
am4core.options.minPolylineStep = 5;

const ChartFour = ({data, name, className}) => {
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        // Create chart instance
        let chart = am4core.create(name, am4charts.XYChart);
        chart.defaultState.transitionDuration = 0;
        chart.hiddenState.transitionDuration = 0;

        chart.events.on('ready', () => {
            setShowLoader(false);
        });

        // Add data
        chart.data = data['data'].sort((a, b) => a.own_local_revenue_change - b.own_local_revenue_change);
        let legend = data['legend'];

        // Create axes
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "gov_entity";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 20;
        categoryAxis.renderer.labels.template.rotation = -90;
        categoryAxis.renderer.labels.template.horizontalCenter = "right";
        categoryAxis.renderer.labels.template.verticalCenter = "middle";

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.labels.template.adapter.add("text", function(text) {
            return text + " %";
        });

        // Create series
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = "own_local_revenue_change";
        series.dataFields.categoryX = "gov_entity";
        series.name = legend['own_local_revenue_change'];
        series.columns.template.tooltipText = "{categoryX}: [bold]{valueY}[/]%";

        // Color ranges and their corresponding column visibility
        const colorRanges = [
            {min: 0, max: 20000, color: '#4a85d0', label: '0 - 20,000'},
            {min: 20001, max: 50000, color: '#ffcc2d', label: '20,001 - 50,000'},
            {min: 50001, max: 100000, color: '#193a0c', label: '50,001 - 100,000'},
            {min: 100001, max: 200000, color: '#d42daa', label: '100,001 - 200,000'},
            {min: 200001, max: Infinity, color: '#F44336', label: 'Tiranë'}
        ];

        // Create custom legend at the bottom of the chart
        const legendContainer = chart.createChild(am4core.Container);
        legendContainer.layout = "horizontal";
        legendContainer.marginTop = 20;
        legendContainer.padding(5, 10, 5, 10);
        legendContainer.width = am4core.percent(100);
        legendContainer.align = "right";
        legendContainer.contentAlign = "center";

        colorRanges.forEach((range) => {
            // Create a rectangle for the legend
            const legendItem = legendContainer.createChild(am4core.Circle);
            legendItem.fill = am4core.color(range.color);
            legendItem.strokeWidth = 1;
            legendItem.stroke = am4core.color("#000");
            legendItem.width = 20; // Width of the legend item
            legendItem.height = 20; // Height of the legend item
            legendItem.marginRight = 5;
            legendItem.cursorOverStyle = am4core.MouseCursorStyle.pointer; // Change cursor on hover

            // Add click event to toggle visibility of columns in the series
            legendItem.events.on("hit", () => {
                let isVisible = false;

                // Check current visibility of columns within the specified range
                for (let column of series.columns) {
                    let value = column.dataItem.dataContext.population; // Get the column value
                    if (value >= range.min && value <= range.max && column.visible) {
                        isVisible = true;
                        break;
                    }
                }

                // Toggle visibility of columns in the range
                for (let column of series.columns) {
                    let value = column.dataItem.dataContext.population; // Get the column value
                    if (value >= range.min && value <= range.max) {
                        column.visible = !isVisible; // Set to opposite of current visibility
                    }
                }

            });

            // Create a label for the legend
            const label = legendContainer.createChild(am4core.Label);
            label.text = range.label; // Set label text
            label.marginLeft = 0; // Margin between rectangle and label
            label.marginRight = 30; // Margin between label and next rectangle
            label.align = "middle"; // Align label vertically
            label.verticalCenter = "middle"; // Center the label
        });

        // Adjust fill color based on the population value
        series.columns.template.adapter.add('fill', (fill, target) => {
            let population = target.dataItem.dataContext.population;

            if (population) {
                let value = population;
                if (value <= 20000) {
                    return am4core.color('#4a85d0'); // Green
                } else if (value >= 20001 && value <= 50000) {
                    return am4core.color('rgb(255,204,45)'); // Yellow
                } else if (value >= 50001 && value <= 100000) {
                    return am4core.color('#193a0c'); // Orange
                } else if (value >= 100001 && value <= 200000) {
                    return am4core.color('#d42daa'); // Orange
                }
                return am4core.color('#F44336'); // Red (for values > 200000)
            }

            return am4core.color('#8a8a8a'); // Default color if no population value
        });

        let columnTemplate = series.columns.template;
        columnTemplate.strokeOpacity = 0;

        chart.exporting.menu = new am4core.ExportMenu();
        chart.exporting.menu.align = 'left';
        chart.exporting.menu.verticalAlign = 'top';

    }, []);

    return (
        <>
            <div id={name} style={{width: showLoader ? '0' : '100%', height: showLoader ? '0' : '700px'}} className={className}></div>
            {showLoader && (
                <Stack spacing={1}>
                    <Skeleton variant='text'/>
                    <Skeleton variant='rounded' width={'auto'} height={500}/>
                </Stack>
            )}
        </>
    );
};

export default ChartFour;
